
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import { useParams } from "react-router-dom";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "../../../assets/css/TestimonialsSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function hasImage(value) {
  return (
    typeof value === "string" &&
    value.trim() !== "" &&
    value !== "undefined" &&
    value !== "null"
  );
}

function TestimonialsSection() {
  const [officialsData, setOfficialsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { section, id } = useParams();

  useEffect(() => {
    let cancelled = false;

    async function fetchOfficialsData() {
      setLoading(true);

      try {
        await axios.get(`${BASE_URL}sanctum/csrf-cookie`).catch(() => null);

        const response = await axios.get(
          `${BASE_URL}pagdata?itemName[eq]=top_manag&status02[eq]=publish`
        );

        if (cancelled) return;

        if (response?.data?.status === 200) {
          setOfficialsData(response.data.allData || []);
        } else {
          setOfficialsData([]);
          console.error(
            "Unable to load leadership information:",
            response?.data?.validation_error
          );
        }
      } catch (error) {
        if (!cancelled) {
          setOfficialsData([]);
          console.error("Unable to load leadership information:", error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchOfficialsData();

    return () => {
      cancelled = true;
    };
  }, [section, id]);

  if (loading) {
    return (
      <section
        id="featured-official"
        className="fmhud-leadership"
        aria-label="Ministry leadership"
      >
        <div className="container">
          <div className="fmhud-leadership-status" role="status">
            Loading leadership information...
          </div>
        </div>
      </section>
    );
  }

  if (officialsData.length === 0) return null;

  return (
    <section
      id="featured-official"
      className="fmhud-leadership"
      aria-label="Ministry leadership"
    >
      <div className="fmhud-leadership-pattern" aria-hidden="true" />
      <div className="fmhud-leadership-accent" aria-hidden="true" />

      <div className="container fmhud-leadership-container">
        <div className="fmhud-leadership-heading">
          <span className="fmhud-leadership-eyebrow">
            LEADERSHIP &amp; SERVICE
          </span>
          <h2>Meet Our Leadership</h2>
          {/* <p>
            Dedicated leadership advancing housing and sustainable urban
            development across Nigeria.
          </p> */}
        </div>

        <Swiper
          modules={[Pagination, Autoplay, EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          pagination={{ clickable: true }}
          autoplay={
            officialsData.length > 1
              ? {
                  delay: 6500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          loop={officialsData.length > 1}
          speed={650}
          watchOverflow
          className="fmhud-leadership-swiper"
        >
          {officialsData.map((official, index) => {
            const imageAvailable = hasImage(official.item01);
            const imageUrl = imageAvailable
              ? `${BACKEND_URL}upload/${official.item01}`
              : null;

            return (
              <SwiperSlide key={official.id || `${official.item03b}-${index}`}>
                <article className="fmhud-leader-slide">
                  <div className="fmhud-leader-portrait-column">
                    <div className="fmhud-leader-portrait-frame">
                      <div className="fmhud-leader-portrait">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={official.item03b || "Ministry official"}
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                          />
                        ) : (
                          <div className="fmhud-leader-image-placeholder">
                            <i
                              className="fa-solid fa-user-tie"
                              aria-hidden="true"
                            />
                            <span>fmhud</span>
                          </div>
                        )}
                      </div>
                      <span
                        className="fmhud-leader-portrait-corner"
                        aria-hidden="true"
                      />
                    </div>
                    <span className="fmhud-leader-portrait-caption">
                      FEDERAL MINISTRY OF HOUSING AND URBAN DEVELOPMENT
                    </span>
                  </div>

                  <div className="fmhud-leader-information">
                    <div className="fmhud-leader-label">
                      <span className="fmhud-leader-label-line" />
                      OFFICIAL PROFILE
                    </div>

                    <h3>{official.item03b || "Ministry Official"}</h3>

                    {official.item03 && (
                      <p className="fmhud-leader-designation">
                        {official.item03}
                      </p>
                    )}

                    <div className="fmhud-leader-divider">
                      <span />
                    </div>

                    {official.linker && (
                      <blockquote className="fmhud-leader-message">
                        <i
                          className="fa-solid fa-quote-left"
                          aria-hidden="true"
                        />
                        <p>{official.linker}</p>
                      </blockquote>
                    )}

                    <div className="fmhud-leader-footer">
                      <span className="fmhud-leader-footer-mark">
                        <i
                          className="fa-solid fa-landmark"
                          aria-hidden="true"
                        />
                      </span>
                      <span>
                        <strong>Housing. Development. Progress.</strong>
                        <small>Serving Nigeria through public leadership</small>
                      </span>
                    </div>
                  </div>
                </article>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
}

export default TestimonialsSection;
